import React, { useState, useRef, useEffect } from "react";
import { FaAngleRight } from "react-icons/fa";
import { ContextMenuType, ContextMenuItem } from "./ContextMenuItem";
import { ContextMenuStyled } from "./ContextMenu";
import { Portal } from "../../components/portal";
import useAbsoluteWindowStore from "../../stores/absoluteWindowStore";

export function ContextMenuItemHover(
    props: ContextMenuType & { secondaryContextMenu?: React.ReactNode }
) {
    // State
    const [isHovering, setIsHovering] = useState(false);

    // Refs
    const contextItemRef = useRef<HTMLDivElement>(null);
    const parentRef = useGetParentRef(contextItemRef);
    const secondContextRef = useRef<HTMLDivElement>(null);

    const [secondContextPos, setSecondContextPos] = useState({
        top: 0,
        left: 0,
    });

    useEffect(() => {
        if (parentRef.current && contextItemRef.current && isHovering) {
            const contextItemRect =
                contextItemRef.current?.getBoundingClientRect();
            const contextMenuRect = parentRef.current?.getBoundingClientRect();
            const secondContextRect =
                secondContextRef.current?.getBoundingClientRect();

            if (!contextItemRect || !contextMenuRect || !secondContextRect)
                return;

            let top = contextItemRect?.top || 0;
            let left = contextMenuRect?.right || 0;

            // Ensure that context menu is always in the window.
            if (
                contextItemRect?.bottom + secondContextRect.height >
                window.innerHeight
            ) {
                top = window.innerHeight - secondContextRect.height;
            }
            if (
                contextMenuRect?.right + secondContextRect.width >
                window.innerWidth
            ) {
                left = window.innerWidth - secondContextRect.width;
            }

            setSecondContextPos({ top, left });
        }
    }, [parentRef, contextItemRef, isHovering]);

    return (
        <div
            ref={contextItemRef}
            onMouseEnter={() => {
                setIsHovering(true);
            }}
            onMouseLeave={() => {
                setIsHovering(false);
            }}
        >
            <ContextMenuItem
                {...props}
                secondaryText={
                    <FaAngleRight size={14} className="translate-x-1" />
                }
                callbackFunction={null}
            />
            {isHovering && (
                <SecondContextMenuForwardRef
                    ref={secondContextRef}
                    position={secondContextPos}
                    children={props.secondaryContextMenu}
                />
            )}
        </div>
    );
}

export const SecondContextMenuForwardRef = React.forwardRef<
    HTMLDivElement,
    {
        position: { left: number; top: number };
        children?: React.ReactNode;
        style?: React.CSSProperties;
    }
>(({ position, children, style }, ref) => {
    return (
        <div >
            <Portal>
                <div
                    id="secondaryContextMenu"
                    style={{
                        pointerEvents: "all",
                        position: "fixed",
                        top: position.top,
                        left: position.left - 30,
                        paddingLeft: "30px", //? This is used so cursor can move from hoverItem to 2nd context menu without losing hover
                        zIndex: 99999,
                        transform: "translateY(-4px)",
                    }}
                >
                    <ContextMenuStyled
                        ref={ref}
                        style={{
                            position: "unset",
                            ...style,
                        }}
                    >
                        {children}
                    </ContextMenuStyled>
                </div>
            </Portal>
        </div>
    );
});

function useGetParentRef(ref: React.RefObject<HTMLDivElement | null>) {
    // Refs
    const parentRef = useRef<HTMLDivElement>(null);

    // Set Parent Ref
    useEffect(() => {
        if (ref.current?.parentElement) {
            const div = document.getElementById(
                "contextMenu"
            ) as HTMLDivElement;
            parentRef.current = div as HTMLDivElement;
        }
    }, [ref]);

    return parentRef;
}
